import { useState, useCallback } from 'react'
import { Head, Link } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import AdminLayout from '@/components/admin/AdminLayout'
import PageHeader from '@/components/ui/PageHeader'
import Notification from '@/components/ui/Notification'
import DataTable, { useFlash, usePagination, type DataTableColumn } from '@/components/ui/DataTable'
import { FormSelect, FormInput } from '@/components/ui/form'
import FormMultiSelect from '@/components/ui/form/FormMultiSelect'
import Button from '@/components/ui/Button'
import Pagination from '@/components/ui/Pagination'
import { Badge } from '@/components/catalyst/badge'
import { Dialog, DialogTitle, DialogBody, DialogActions } from '@/components/catalyst/dialog'
import { PencilIcon, TrashIcon, ShieldCheckIcon } from '@heroicons/react/24/outline'

// ─── Types ──────────────────────────────────────

interface RoleRow {
  id: number
  name: string
  permissions_count: number
  users_count: number
  permission_ids: number[]
}

interface PermissionOption { value: number; label: string }

interface Props {
  roles: RoleRow[]
  all_permissions: PermissionOption[]
}

const PAGE_SIZES = [30, 50, 100, 200]

// ─── Component ──────────────────────────────────

export default function RolesIndex({ roles: initialRoles, all_permissions }: Props) {
  const [roles, setRoles] = useState(initialRoles)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [formName, setFormName] = useState('')
  const [formPermissionIds, setFormPermissionIds] = useState<(string | number)[]>([])
  const [formErrors, setFormErrors] = useState<string[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [notifMessage, setNotifMessage] = useState('')
  const [showNotif, setShowNotif] = useState(false)
  const { flashRow } = useFlash()

  const csrfToken = typeof document !== 'undefined'
    ? document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    : ''
  const notify = useCallback((msg: string) => { setNotifMessage(msg); setShowNotif(true) }, [])

  const pageSizeOptions = PAGE_SIZES.map(n => ({ value: String(n), label: `${n} per page` }))
  const { pageSize, setPageSize, currentPage, setCurrentPage, totalPages, paginatedRows, totalCount } = usePagination(roles)

  // ─── CRUD ───────────────────────────────────
  const openCreate = () => {
    setFormName(''); setFormPermissionIds([])
    setFormErrors([]); setDialogOpen(true)
  }

  const handleSubmit = async () => {
    if (!formName.trim()) { setFormErrors(['Name is required']); return }
    setFormErrors([]); setSubmitting(true)
    try {
      const res = await fetch('/admin/roles.json', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({ role: { name: formName.trim(), permission_ids: formPermissionIds.map(Number) } }),
      })
      if (res.ok) {
        const data = await res.json()
        setRoles(prev => [data, ...prev])
        setDialogOpen(false)
        notify('Saved:Role created')
      } else {
        const err = await res.json().catch(() => ({}))
        setFormErrors(err.errors || ['Failed to save'])
      }
    } catch { setFormErrors(['Network error']) }
    setSubmitting(false)
  }

  const deleteRole = useCallback(async (id: number) => {
    if (!confirm('Are you sure you want to delete this role?')) return
    try {
      const res = await fetch(`/admin/roles/${id}.json`, { method: 'DELETE', headers: { 'X-CSRF-Token': csrfToken } })
      if (res.ok) { setRoles(prev => prev.filter(r => r.id !== id)); notify('Deleted:Role removed') }
    } catch { /* silent */ }
  }, [csrfToken, notify])

  // ─── Columns ────────────────────────────────
  const columns: DataTableColumn<RoleRow>[] = [
    {
      key: 'name', label: 'Name',
      render: row => (
        <Link href={`/admin/roles/${row.id}`} className="font-medium text-indigo-600 hover:text-indigo-500 hover:underline dark:text-indigo-400">
          {row.name}
        </Link>
      ),
      sortValue: row => row.name,
      filterValue: row => row.name,
    },
    {
      key: 'permissions_count', label: 'Permissions',
      render: row => <Badge color="purple">{row.permissions_count}</Badge>,
      sortValue: row => row.permissions_count,
    },
    {
      key: 'users_count', label: 'Users',
      render: row => <Badge color="blue">{row.users_count}</Badge>,
      sortValue: row => row.users_count,
    },
  ]

  return (
    <>
      <Head title="Admin - Roles" />
      <Notification message={notifMessage} show={showNotif} onClose={() => setShowNotif(false)} />
      <AdminLayout>
      <main>
      <PageHeader title="Roles" icon={<ShieldCheckIcon className="h-5 w-5" />} actions={
        <div className="flex items-center gap-2">
          <div className="w-36"><FormSelect value={String(pageSize)} onChange={v => { setPageSize(Number(v)); setCurrentPage(0) }} options={pageSizeOptions} /></div>
          <Button onClick={openCreate}>+ New Role</Button>
        </div>
      } />

      <div className="mb-2"><Badge color="zinc"><span className="text-xs font-medium">{totalCount} roles</span></Badge></div>

      <DataTable
        rows={paginatedRows} columns={columns} rowKey={row => row.id}
        flashRowId={flashRow?.id ?? null} flashStatus={flashRow?.status ?? null}
        emptyText="No roles" filteredEmptyText="No roles match your filters"
        actions={row => (
          <div className="flex items-center justify-end gap-1">
            <Link href={`/admin/roles/${row.id}`} className="rounded p-1 text-zinc-400 hover:text-indigo-600 dark:text-zinc-500 dark:hover:text-indigo-400" title="Edit"><PencilIcon className="h-4 w-4" /></Link>
            <button onClick={() => deleteRole(row.id)} className="rounded p-1 text-zinc-400 hover:text-red-600 dark:text-zinc-500 dark:hover:text-red-400" title="Delete"><TrashIcon className="h-4 w-4" /></button>
          </div>
        )}
      />

      <Pagination currentPage={currentPage} totalPages={totalPages} pageSize={pageSize} totalCount={totalCount} onPageChange={setCurrentPage} />

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>New Role</DialogTitle>
        <DialogBody>
          {formErrors.length > 0 && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2 dark:border-red-800 dark:bg-red-900/20">
              <ul className="list-disc pl-4 text-xs text-red-700 dark:text-red-400">{formErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
            </div>
          )}
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Name *</label>
              <FormInput value={formName} onChange={e => setFormName((e.target as HTMLInputElement).value)} placeholder="Role name..." autoFocus />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-zinc-700 dark:text-zinc-300">Permissions</label>
              <FormMultiSelect
                values={formPermissionIds}
                options={all_permissions.map(p => ({ value: p.value, label: p.label }))}
                onChange={setFormPermissionIds}
                placeholder="Search permissions..."
              />
            </div>
          </div>
        </DialogBody>
        <DialogActions>
          <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button disabled={submitting} onClick={handleSubmit}>{submitting ? 'Saving...' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
      </main>
      </AdminLayout>
    </>
  )
}

RolesIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
